import { useEffect, useRef, useState } from "react";
import { pointInRect, type Rect } from "./heroGeometry";
import type { PlayerScreen, SceneRuntime } from "./sceneRuntime";

// Landing-hero stadium scene (EPIC-102 / PMA-PUB-023). Renders inside
// `home-hero-stage`: a static fallback first, then a lazily imported three.js
// runtime (its own chunk) fades a canvas in over it. Background only
// (EPIC-105 / PMA-PUB-023): a pointer moves the camera, nothing is clickable.

declare global {
  interface Window {
    __stadiumHero?: {
      readonly ready: boolean;
      readonly renderCount: number;
      /** Each player's id and centre in viewport CSS px. */
      players: () => Array<PlayerScreen & { pickable: boolean }>;
    };
  }
}

function webglAvailable(): boolean {
  try {
    const probe = document.createElement("canvas");
    const gl = (probe.getContext("webgl2") || probe.getContext("webgl")) as
      | WebGLRenderingContext
      | WebGL2RenderingContext
      | null;
    if (!gl) return false;
    gl.getExtension("WEBGL_lose_context")?.loseContext();
    return true;
  } catch {
    return false;
  }
}

function prefersReducedMotion(): boolean {
  try {
    return (
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false
    );
  } catch {
    return false;
  }
}

type ScrimBox = Rect;

const SCRIM_PAD = 20;

export default function StadiumHero() {
  const rootRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const runtimeRef = useRef<SceneRuntime | null>(null);
  const readyRef = useRef(false);
  const [ready, setReady] = useState(false);
  const [scrimBox, setScrimBox] = useState<ScrimBox | null>(null);
  const scrimRef = useRef<ScrimBox | null>(null);

  useEffect(() => {
    scrimRef.current = scrimBox;
  }, [scrimBox]);

  // The scrim is a flat navy panel sized to the hero's text content (eyebrow,
  // headline, subhead, CTAs, link), so white text never meets a pitch line.
  useEffect(() => {
    const root = rootRef.current;
    const hero = root?.parentElement?.querySelector<HTMLElement>(
      '[data-testid="home-hero"]',
    );
    if (!root || !hero) return undefined;
    const measure = () => {
      const base = root.getBoundingClientRect();
      let l = Infinity;
      let t = Infinity;
      let r = -Infinity;
      let b = -Infinity;
      hero.querySelectorAll<HTMLElement>("*").forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.width === 0 || rect.height === 0) return;
        l = Math.min(l, rect.left);
        t = Math.min(t, rect.top);
        r = Math.max(r, rect.right);
        b = Math.max(b, rect.bottom);
      });
      if (!Number.isFinite(l) || base.width === 0) {
        setScrimBox(null);
        return;
      }
      setScrimBox((prev) => {
        const next = {
          left: Math.round(l - base.left - SCRIM_PAD),
          top: Math.round(t - base.top - SCRIM_PAD),
          width: Math.round(r - l + SCRIM_PAD * 2),
          height: Math.round(b - t + SCRIM_PAD * 2),
        };
        return prev &&
          prev.left === next.left &&
          prev.top === next.top &&
          prev.width === next.width &&
          prev.height === next.height
          ? prev
          : next;
      });
    };
    measure();
    const obs =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(measure)
        : null;
    obs?.observe(hero);
    obs?.observe(root);
    window.addEventListener("resize", measure);
    return () => {
      obs?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    let runtime: SceneRuntime | null = null;
    if (!webglAvailable()) return undefined;
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const reduced = prefersReducedMotion();

    void import("./sceneRuntime")
      .then((mod) => {
        if (cancelled) return;
        try {
          runtime = mod.createSceneRuntime({
            canvas,
            reducedMotion: reduced,
            onFirstFrame: () => {
              readyRef.current = true;
              if (!cancelled) setReady(true);
            },
            onContextLost: () => {
              readyRef.current = false;
              runtime?.dispose();
              runtime = null;
              runtimeRef.current = null;
              if (!cancelled) {
                setReady(false);
              }
            },
          });
        } catch {
          runtime = null;
          return;
        }
        runtimeRef.current = runtime;
        if (reduced) runtime.renderStill();
        else runtime.start();
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
      runtime?.dispose();
      runtimeRef.current = null;
      readyRef.current = false;
    };
  }, []);

  // Test hook: only in E2E builds. The literal comparison lets the bundler
  // drop this whole block from production output.
  useEffect(() => {
    if (import.meta.env.VITE_E2E_TEST_MODE !== "true") return undefined;
    const hook = {
      get ready() {
        return readyRef.current;
      },
      get renderCount() {
        return runtimeRef.current?.renderCount ?? 0;
      },
      players: () => {
        const rect = canvasRef.current?.getBoundingClientRect();
        const list = runtimeRef.current?.playerScreens() ?? [];
        return list.map((p) => ({
          id: p.id,
          x: p.x + (rect?.left ?? 0),
          y: p.y + (rect?.top ?? 0),
          pickable: !pointInRect(p.x, p.y, scrimRef.current),
        }));
      },
    };
    window.__stadiumHero = hook;
    return () => {
      delete window.__stadiumHero;
    };
  }, []);

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const runtime = runtimeRef.current;
    const rect = rootRef.current?.getBoundingClientRect();
    if (!runtime || !rect || rect.width === 0 || rect.height === 0) return;
    runtime.setPointer(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      ((event.clientY - rect.top) / rect.height) * 2 - 1,
    );
  };

  return (
    <div
      ref={rootRef}
      data-testid="stadium-hero"
      className="absolute inset-0"
      onPointerMove={onPointerMove}
      onPointerLeave={() => runtimeRef.current?.setPointer(null)}
    >
      <div
        data-testid="stadium-hero-fallback"
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 80% 60% at 65% 55%, #16407a 0%, #0b1e3f 55%, #050b1a 100%)",
        }}
      >
        <svg
          className="h-full w-full opacity-60"
          viewBox="0 0 105 68"
          preserveAspectRatio="xMidYMid slice"
          fill="none"
          stroke="#cfe6ff"
          strokeWidth="0.25"
        >
          <rect x="4" y="4" width="97" height="60" />
          <line x1="52.5" y1="4" x2="52.5" y2="64" />
          <circle cx="52.5" cy="34" r="9.15" />
          <rect x="4" y="13.8" width="16.5" height="40.3" />
          <rect x="84.5" y="13.8" width="16.5" height="40.3" />
        </svg>
      </div>
      <canvas
        ref={canvasRef}
        data-testid="stadium-hero-canvas"
        data-ready={ready ? "true" : undefined}
        aria-hidden="true"
        className={`absolute inset-0 h-full w-full transition-opacity duration-700 ${ready ? "opacity-100" : "opacity-0"}`}
      />
      <div
        data-testid="stadium-hero-scrim"
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={
          scrimBox
            ? {
                inset: "auto",
                left: scrimBox.left,
                top: scrimBox.top,
                width: scrimBox.width,
                height: scrimBox.height,
                borderRadius: 28,
                background: "rgba(11, 30, 63, 0.8)",
                backdropFilter: "blur(3px)",
                WebkitBackdropFilter: "blur(3px)",
                boxShadow: "0 0 56px 44px rgba(11, 30, 63, 0.5)",
              }
            : {
                background:
                  "radial-gradient(ellipse 66% 54% at 50% 42%, rgba(11, 30, 63, 0.94) 0%, rgba(11, 30, 63, 0.94) 58%, rgba(11, 30, 63, 0) 100%)",
              }
        }
      />
      <div
        data-testid="stadium-hero-bottom-fade"
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 bottom-0 h-[10%] bg-gradient-to-b from-transparent to-surface-dark-page portrait:h-[40%] portrait:via-surface-dark-page portrait:via-30% portrait:to-surface-dark-page"
      />
    </div>
  );
}
